import styled from "@emotion/styled";
import { Avatar, H2, H3, TagLink, TagList, Text } from "atoms";
import { useSystemStats } from "../api/useSystemStats";
import trendingFounderAttributes from "../data/trendingFounderAttributes.json";
import trendingMissionOrCause from "../data/trendingMissionOrCause.json";

const formatReviewerName = (name?: string) => {
  if (!name) {
    return "Unknown";
  }
  if (name.length > 15) {
    return `${name.slice(0, 8)}...${name.slice(name.length - 6, name.length)}`;
  }
  return name;
};

function PlatformStats() {
  const { data } = useSystemStats();
  const topReviewers = data?.topReviewers?.slice(0, 4) || [];
  return (
    <ThreeCols>
      <div>
        <SH2>Top Reviewers</SH2>
        <VerticalAlignGrid>
          {topReviewers.map(({ name, reviews, avatar }) => (
            <Curator key={name}>
              <Avatar src={avatar} alt={name} />
              <div>
                <SH3 size="sm">{formatReviewerName(name)}</SH3>
                <Text size="xxs">{reviews} reviews</Text>
              </div>
            </Curator>
          ))}
        </VerticalAlignGrid>
      </div>

      <div>
        <SH2>HUG Stats</SH2>
        <VerticalAlignGrid>
          <Stat>
            <Strong>{data?.totalProjects || "?"}</Strong> <span>Projects</span>
          </Stat>
          <Stat>
            <Strong>{data?.totalArtists || "?"}</Strong> <span>Artists</span>
          </Stat>
          <Stat>
            <Strong>{data?.totalReviews || "?"}</Strong> <span>Reviews</span>
          </Stat>
          <Stat>
            <Strong>{data?.totalHuggers || "?"}</Strong> <span>Huggers</span>
          </Stat>
        </VerticalAlignGrid>
      </div>

      <div>
        <SH2>Trending Tags</SH2>
        <TagList>
          {trendingFounderAttributes.map((tag) => (
            <TagLink key={tag} tag={tag} variant="founderAttributes" />
          ))}
          {trendingMissionOrCause.map((tag) => (
            <TagLink key={tag} tag={tag} variant="missionOrCause" />
          ))}
        </TagList>
      </div>
    </ThreeCols>
  );
}

const ThreeCols = styled
  .div({
    display: "grid",
    gap: "4rem",
    "@media (min-width: 450px) and (max-width: 799px)": {
      gridTemplateColumns: "4fr 3fr",
      gap: "3rem 2rem",
      "&>section:last-of-type": {
        gridColumn: "1 / -1",
      },
    },
    "@media (min-width: 800px)": {
      gridTemplateColumns: "3fr 2fr 4fr",
      gap: "2rem",
    },
  })
  .withComponent("div");
const VerticalAlignGrid = styled.div({
  display: "grid",
  gridTemplateRows: "repeat(4, 50px)",
  gap: 10,
});
const SH2 = styled(H2)(({ theme }) => ({
  ...theme.fonts.body,
  fontSize: theme.fontSizes.sm,
  fontWeight: "bold",
  marginBottom: theme.spacing.lg,
}));

const SH3 = styled(H3)(({ theme }) => ({
  ...theme.fonts.display,
  marginBottom: 0,
}));

const Curator = styled.article({
  alignItems: "center",
  display: "flex",
  gap: ".5rem",
  "*": {
    marginBottom: 0,
  },
  p: {
    margin: 0,
    opacity: ".8",
  },
});
const Stat = styled(Text)({
  height: 50,
  display: "flex",
  alignItems: "center",
  marginBottom: 0,
  paddingBottom: "1em",
  "h3,h4": {
    marginBottom: 0,
  },
  "@media (min-width: 960px)": {
    height: "55px",
  },
});
const Strong = styled.strong(({ theme }) => ({
  ...theme.fonts.display,
  fontSize: theme.fontSizes.lg,
  fontWeight: "bold",
  "& + span": {
    marginLeft: "0.5rem",
  },
}));

export { PlatformStats };
